/**
 * WebPushToggle — small button in the top bar that lets the user enable/disable
 * Firebase Web Push notifications.
 *
 * • not subscribed (Notification.permission==="default")
 *      → shows BellOff with "Povolit notifikace" tooltip; click triggers permission flow
 * • subscribed (granted + token in localStorage)
 *      → shows green Bell with "Notifikace aktivní" tooltip
 * • denied
 *      → shows red BellOff with "Notifikace blokovány v prohlížeči" tooltip
 *
 * Also hooks `onForegroundMessage` so push payloads received while the tab is
 * focused are surfaced as a sonner toast instead of a system notification.
 */
import React, { useEffect, useState } from "react";
import { Bell, BellOff, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  enableWebPush, disableWebPush, getWebPushStatus,
  onForegroundMessage, isWebPushEnabledLocally,
} from "../../../config/webPush";

export default function WebPushToggle() {
  const [permission, setPermission] = useState(getWebPushStatus());
  const [enabled, setEnabled] = useState(isWebPushEnabledLocally());
  const [busy, setBusy] = useState(false);

  // Foreground notifications → sonner toast with click-through link
  useEffect(() => {
    let unsub = () => {};
    if (enabled) {
      onForegroundMessage((payload) => {
        const title = payload?.notification?.title || payload?.data?.title || "CRM";
        const body  = payload?.notification?.body  || payload?.data?.body  || "";
        toast(title, { description: body, duration: 8000, action: payload?.data?.url ? {
          label: "Otevřít",
          onClick: () => window.location.assign(payload.data.url),
        } : undefined });
      }).then((u) => { if (typeof u === "function") unsub = u; });
    }
    return () => unsub();
  }, [enabled]);

  if (permission === "unsupported") return null;

  const handleClick = async () => {
    if (busy) return;
    setBusy(true);
    if (enabled) {
      const ok = await disableWebPush();
      if (ok) setEnabled(false);
    } else {
      const ok = await enableWebPush();
      if (ok) {
        setEnabled(true);
        setPermission("granted");
      } else {
        setPermission(getWebPushStatus());
      }
    }
    setBusy(false);
  };

  let tooltip = "Povolit webové notifikace";
  let cls = "text-slate-500 hover:bg-slate-100";
  let Icon = BellOff;
  if (enabled) {
    tooltip = "Notifikace aktivní (klik = vypnout)";
    cls = "text-emerald-600 hover:bg-emerald-50";
    Icon = Bell;
  } else if (permission === "denied") {
    tooltip = "Notifikace zablokovány — povolte je v nastavení prohlížeče (zámek vedle adresy)";
    cls = "text-red-500 hover:bg-red-50";
  }

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      title={tooltip}
      data-testid="web-push-toggle"
      className={`p-2 rounded-md transition-colors ${cls} disabled:opacity-50`}
    >
      {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4" />}
    </button>
  );
}
